import React from 'react';
import './styles.css';
import ScrollContainer from './components/ScrollContainer';
import MenuItems from './components/MenuItems';
import useIntersectionObserver from './hooks/useIntersectionObserver';
import useItemsChanged from './hooks/useItemsChanged';
import createApi from './createApi';
import ItemsMap from './ItemsMap';
import { observerOptions as defaultObserverOptions } from './settings';
import * as constants from './constants';
import useOnInitCb from './hooks/useOnInitCb';
import useOnUpdate from './hooks/useOnUpdate';
import { VisibilityContext } from './context';
import { getElementOrConstructor } from './helpers';
import slidingWindow from './slidingWindow';
import getItemsPos from './getItemsPos';
const apiRefDefault = { current: {} };
const cbDefault = () => void 0;
/**
  See docs and examples at

  https://github.com/asmyshlyaev177/react-horizontal-scrolling-menu
 */
function ScrollMenu({ LeftArrow: _LeftArrow, RightArrow: _RightArrow, children, Header: _Header, Footer: _Footer, transitionDuration = 500, transitionEase, transitionBehavior, onInit = cbDefault, onUpdate = cbDefault, onMouseDown, onMouseUp, onMouseMove, onScroll = cbDefault, onTouchMove, onTouchStart, onTouchEnd, onWheel = cbDefault, options = defaultObserverOptions, scrollContainerClassName = '', itemClassName = '', separatorClassName = '', wrapperClassName = '', apiRef = apiRefDefault, RTL, noPolyfill, }) {
    const LeftArrow = getElementOrConstructor(_LeftArrow);
    const RightArrow = getElementOrConstructor(_RightArrow);
    const Header = getElementOrConstructor(_Header);
    const Footer = getElementOrConstructor(_Footer);
    const scrollContainerRef = React.useRef(null);
    const [menuItemsRefs] = React.useState({});
    const observerOptions = React.useMemo(() => (Object.assign(Object.assign(Object.assign({}, defaultObserverOptions), options), { root: scrollContainerRef.current })), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, scrollContainerRef.current]);
    const items = React.useRef(new ItemsMap()).current;
    // NOTE: hack for detect when items added/removed dynamicaly
    const itemsChanged = useItemsChanged(children, items);
    const ioOptions = React.useMemo(() => ({
        items,
        itemsChanged,
        options: observerOptions,
        refs: menuItemsRefs,
    }), [items, itemsChanged, menuItemsRefs, observerOptions]);
    const visibleElementsWithSeparators = useIntersectionObserver(ioOptions);
    const mounted = !!visibleElementsWithSeparators.length;
    const api = React.useMemo(() => createApi(items, visibleElementsWithSeparators, scrollContainerRef, {
        duration: transitionDuration,
        ease: transitionEase,
        behavior: transitionBehavior,
    }, RTL, noPolyfill), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, visibleElementsWithSeparators, itemsChanged, RTL, noPolyfill]);
    const getContext = React.useCallback(() => (Object.assign(Object.assign({}, api), { initComplete: mounted, items,
        visibleElementsWithSeparators, scrollContainer: scrollContainerRef })), [api, mounted, items, visibleElementsWithSeparators, scrollContainerRef]);
    const [context, setContext] = React.useState(getContext);
    const onInitCbFired = useOnInitCb({
        cb: () => onInit(context),
        condition: mounted,
    });
    useOnUpdate({
        cb: () => onUpdate(context),
        condition: onInitCbFired,
        hash: JSON.stringify(visibleElementsWithSeparators
            .concat(String(context === null || context === void 0 ? void 0 : context.isFirstItemVisible))
            .concat(String(context === null || context === void 0 ? void 0 : context.isLastItemVisible))),
    });
    React.useEffect(() => setContext(getContext()), [getContext]);
    apiRef.current = context;
    const scrollHandler = React.useCallback((event) => onScroll(context, event), [onScroll, context]);
    const onWheelHandler = React.useCallback((event) => onWheel(context, event), [onWheel, context]);
    const wrapperClass = React.useMemo(() => `${constants.wrapperClassName} ${wrapperClassName}`, [wrapperClassName]);
    const containerClassName = React.useMemo(() => `${scrollContainerClassName}${RTL ? ' rtl' : ''}`, [RTL, scrollContainerClassName]);
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", { className: wrapperClass, onWheel: onWheelHandler, onMouseDown: onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(context), onMouseUp: onMouseUp === null || onMouseUp === void 0 ? void 0 : onMouseUp(context), onMouseMove: onMouseMove === null || onMouseMove === void 0 ? void 0 : onMouseMove(context), onTouchStart: onTouchStart === null || onTouchStart === void 0 ? void 0 : onTouchStart(context), onTouchMove: onTouchMove === null || onTouchMove === void 0 ? void 0 : onTouchMove(context), onTouchEnd: onTouchEnd === null || onTouchEnd === void 0 ? void 0 : onTouchEnd(context) },
        React.createElement(VisibilityContext.Provider, { value: context },
            React.createElement("div", { className: constants.headerClassName }, Header),
            React.createElement("div", { className: constants.innerWrapperClassName },
                React.createElement("div", { className: constants.arrowLeftClassName }, LeftArrow),
                React.createElement(ScrollContainer, { className: containerClassName, onScroll: scrollHandler, scrollRef: scrollContainerRef },
                    React.createElement(MenuItems, { refs: menuItemsRefs, itemClassName: itemClassName, separatorClassName: separatorClassName }, children)),
                React.createElement("div", { className: constants.arrowRightClassName }, RightArrow)),
            React.createElement("div", { className: constants.footerClassName }, Footer))));
}
export { constants, getItemsPos, slidingWindow, ScrollMenu, VisibilityContext };
